import { SetStateAction, useRef, useState } from "react";

const NO_EVENT = Symbol("NO_EVENT");

export function useControllableState<Value>(
  userValue?: Value,
  defaultValue?: Value,
  onChange?: (value: Value) => void,
): [Value | undefined, (action: SetStateAction<Value>) => void];

export function useControllableState<Value, Event>(
  userValue?: Value,
  defaultValue?: Value,
  onChange?: (value: Value, event: Event) => void,
): [Value | undefined, (action: SetStateAction<Value>, event: Event) => void];

export function useControllableState<Value, Event>(
  userValue?: Value,
  defaultValue?: Value,
  onChange?: (value: Value, event?: Event) => void,
) {
  const isControlled = useRef(typeof userValue !== "undefined").current;
  if (process.env.NODE_ENV !== "production") {
    const isStillControlled = typeof userValue !== "undefined";
    if (isControlled !== isStillControlled) {
      // eslint-disable-next-line no-console
      console.error(
        `A component is changing an ${
          isControlled ? "controlled" : "uncontrolled"
        } input to be ${isStillControlled ? "controlled" : "uncontrolled"}. ` +
          "Input elements should not switch from uncontrolled to controlled (or vice versa). " +
          "Decide between using a controlled or uncontrolled input element for the lifetime of the component. " +
          "More info: https://reactjs.org/link/controlled-components",
      );
    }
  }

  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = isControlled ? userValue : internalValue;
  const state =
    useRef<
      [
        Value | undefined,
        ((value: Value, event?: Event) => void) | undefined,
        boolean,
      ]
    >(null);
  state.current = [value, onChange, isControlled];

  const setValue = (
    action: Value | ((prevValue: Value | undefined) => Value),
    event: Event | typeof NO_EVENT = NO_EVENT,
  ) => {
    const nextValue =
      action instanceof Function ? action(state.current?.[0]) : action;

    if (nextValue === state.current?.[0]) {
      return;
    }

    const onChange = state.current?.[1];
    const isControlled = state.current?.[2];

    if (event !== NO_EVENT) {
      onChange?.(nextValue, event);
    } else {
      onChange?.(nextValue);
    }
    if (!isControlled) {
      setInternalValue(nextValue);
    }
  };

  return [value, setValue] as const;
}
